import { apiGet, apiUpload, apiDelete } from "../api-client"

/**
 * Valid document types accepted by POST /api/documents/upload/
 * Matches SUPPORTED_TYPES in apps/public_core/views/document_upload.py
 */
export type DocumentType =
  | "w2"
  | "w15"
  | "gau"
  | "schematic"
  | "formation_tops"
  | "w3"
  | "w3a"
  | "other"

/**
 * Success response shape from POST /api/documents/upload/
 */
export interface UploadResponse {
  success: boolean
  message: string
  document_id?: string
  extracted_document_id?: string
  api_number?: string
  document_type?: string
  is_public?: boolean
  vectors_created?: number
  storage_path?: string
  warnings?: string[]
}

export interface UploadWarningResponse {
  status: "warning"
  warning_code: "api_not_found" | "api_mismatch"
  extracted_api?: string
  reasons: string[]
}

/**
 * Upload a document to /api/documents/upload/ via multipart FormData.
 *
 * @param apiNumber   - The well API number (sent as `api_number` field)
 * @param file        - The PDF file to upload
 * @param documentType - One of the valid DocumentType values
 */
export async function uploadDocument(
  apiNumber: string,
  file: File,
  documentType: DocumentType,
  confirmed = false,
): Promise<UploadResponse | UploadWarningResponse> {
  const formData = new FormData()
  formData.append("api_number", apiNumber)
  formData.append("file", file)
  formData.append("document_type", documentType)
  if (confirmed) formData.append("confirmed", "true")

  return apiUpload<UploadResponse | UploadWarningResponse>("/api/documents/upload/", formData)
}

export interface ExtractedDocumentSummary {
  id: number
  document_type: string | null
  source_type: string
  status: string
  api_number: string
  created_at: string
  is_public: boolean
  file_name: string | null
}

export interface DocumentListResponse {
  documents: ExtractedDocumentSummary[]
  total: number
}

export async function listWellDocuments(
  api14: string,
  source?: 'research' | 'tenant',
): Promise<DocumentListResponse> {
  const params = new URLSearchParams({ api_number: api14 })
  if (source) params.set('source', source)
  return apiGet<DocumentListResponse>(`/api/documents/?${params}`)
}

/**
 * Delete a tenant-uploaded document.
 * Calls DELETE /api/documents/<id>/
 */
export async function deleteDocument(docId: number): Promise<void> {
  await apiDelete<void>(`/api/documents/${docId}/`)
}
